/* ============================================================
   SETTING — SESAAT Studio v3.9
   ============================================================
   Fail ini berkhidmat kepada DUA laluan, dikawal oleh `modSebenar`
   (corak yang SAMA seperti ExtensionV39View dan MarketplaceV39View —
   jangan cipta corak baharu):

     modSebenar = false  →  /settings-v3.9  (PELAN reka bentuk)
     modSebenar = true   →  /profile/*      (HALAMAN HIDUP, bila
                                             suis config/ui_v39 hidup)

   Rupa lama tinggal dalam ProfileView.jsx dan TIDAK dipadam — itu
   yang menjadikan patah balik percuma: matikan suis dan ia kembali
   serta-merta, tanpa deploy.

   ⛔ TIADA logik Firebase dalam fail ini. Semua operasi (tukar emel,
      kata laluan, avatar, storage, probe extension) datang daripada
      useAccountSettings — otak yang SAMA dengan rupa lama. Kalau
      perangai perlu diubah, ubah di HOOK. Dua salinan logik akaun
      bermakna satu daripadanya akan terlepas pembetulan, dan
      kegagalannya senyap.

   Susunan diambil daripada app.cal.com/settings, TANPA halaman
   "Overview" (arahan user 2026-08-10): setting mengambil alih satu
   skrin penuh, menu dikumpul di kiri ikut tajuk berikon, klik
   Settings terus mendarat di Profil.

   Halaman "Paparan" DIPULANGKAN 2026-08-15: v3.9 kini ada tema
   TERANG sebenar (tokens.css blok `html.s-light` — nilai diukur
   dari cal.com mod terang), jadi pemilih di sini bukan lagi kawalan
   palsu. **TERANG ialah lalai** untuk semua pengguna (keputusan user
   2026-08-17, membalikkan lalai gelap 15 Ogos); pilihan disimpan
   per-browser (`sesaat_theme_v39`) dan dipasang awal oleh main.jsx.
   Suis ini BERASINGAN daripada toggle tema UI lama (kunci `theme`,
   kelas `dark`) — jangan cantumkan.
   ============================================================ */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Mail, MessageCircle, User, Users, Shield, CreditCard, Puzzle,
    Pencil, Save, X, Send, Camera, Trash2, RefreshCw, Check,
    Eye, EyeOff, Download, ChevronRight, LogOut, HardDrive, Gauge, Info, Palette,
} from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, Card, CardBody, CardFoot, NavRow, ContentPanel, SuisBahasa } from '../design/ui';
import { klikBiasa } from '../utils/klikBiasa';
import KadProStudioPercuma from './KadProStudioPercuma';
import { tarikhPapar } from '../utils/tarikhPapar';
import { useFlowLangganan } from '../hooks/useFlowLangganan';
import { APP_VERSION } from '../version';
import { useT, useLang, SENARAI_BAHASA } from '../i18n';
import {
    useAccountSettings, fmtBytes,
    STORAGE_LIMIT_BYTES, DAILY_PAID_CAP, TRIAL_COIN_CAP, EXTENSION_ZIP_URL,
} from '../hooks/useAccountSettings';

/* Alamat lama → halaman sebenar. `/profile/notifications` = bookmark
   lama; ia mendarat di Paparan, sama seperti pemetaan rupa lama. */
const TAB_ALIAS = { notifications: 'appearance' };

/* Senarai halaman yang SAH. Ia menyimpan KUNCI kamus, bukan ayat —
   pemalar aras-modul tidak boleh memanggil useT(), jadi terjemahan
   berlaku pada masa render di bawah. */
const HALAMAN_SAH = ['account', 'appearance', 'security', 'billing', 'storage', 'extension', 'referral'];

const TIER_LABEL = {
    admin: 'System Admin', moderator: 'Moderator', paid: 'Paid Client', free: 'Free',
};

/* ── Data contoh untuk PELAN reka bentuk ──────────────────────────────
   Bentuknya sama dengan pulangan useAccountSettings supaya JSX di bawah
   hanya ada SATU laluan. Semua pengendali di sini tidak berbuat apa-apa
   — pelan reka bentuk tidak boleh menyentuh akaun sebenar sesiapa. */
const nop = () => { };
const CONTOH = {
    user: { email: 'pelanggan@contoh.com' },
    userData: { whatsappNumber: '+60123456789' },
    loading: false,
    tier: 'paid', isAdmin: false, isPaid: true, plan: 'yearly', licType: 'monthly',
    displayName: 'Nama Pelanggan', initials: 'NP', currentPhotoURL: null,
    joinedDate: '11 Mac 2026', expiryDate: '11 Mac 2027',
    storageUsed: 1.24 * 1024 * 1024 * 1024, storageRatio: 0.62,
    usedToday: 34, trialCoins: 5,
    kuotaSlot: 6, slotTanpaHad: false, browserDigunakan: 4,
    avatarKuota: 6, avatarDigunakan: 3,
    modulSaya: [
        { id: 'ai8saat', nama: 'AI8SAAT' },
        { id: 'ai16saat-half', nama: 'AI16SAAT' },
        { id: 'homeniche', nama: 'HOME NICHE' },
        { id: 'smartcustom', nama: 'SMART' },
    ],
    referrerInfo: { name: 'Ejen Contoh', refCode: 'AFR-XXXXX', whatsappNumber: '+60198887777' },
    loadingReferrer: false,
    extState: { status: 'connected', version: APP_VERSION, responders: 1 },
    runExtProbe: nop,
    avatarInputRef: { current: null }, avatarUploading: false,
    handleAvatarUpload: nop, handleRemoveAvatar: nop,
    isEditingName: false, editName: '', setEditName: nop, nameSaving: false, nameMsg: { type: '', text: '' },
    startEditName: nop, handleSaveName: nop, cancelEditName: nop,
    isEditingEmail: false, editEmail: '', setEditEmail: nop, emailSaving: false, emailMsg: { type: '', text: '' },
    startEditEmail: nop, handleSaveEmail: nop, cancelEditEmail: nop,
    isEditingWhatsapp: false, editWhatsapp: '', setEditWhatsapp: nop, whatsappSaving: false, whatsappMsg: { type: '', text: '' },
    startEditWhatsapp: nop, handleSaveWhatsapp: nop, cancelEditWhatsapp: nop,
    hasPassword: true, passwordData: { current: '', new: '', confirm: '' }, setPasswordData: nop,
    showPasswords: { current: false, new: false, confirm: false }, setShowPasswords: nop,
    passwordLoading: false, passwordMsg: { type: '', text: '' },
    passwordStrength: { score: 0, label: '', pct: 0 },
    passwordValidation: { minLength: false, matches: false, mismatch: false },
    canSubmitPassword: false,
    handleChangePassword: nop, handleSetPassword: nop,
    resetEmailLoading: false, resetEmailSent: false, handleResetPasswordEmail: nop,
    showReauthModal: false, reauthPassword: '', setReauthPassword: nop,
    reauthLoading: false, reauthError: '', reauthAction: null,
    handleReauthAndExecute: nop, closeReauthModal: nop,
    loggingOut: false, handleLogout: nop,
};

/* ── Blok binaan ───────────────────────────────────────────────────── */

const Lbl = ({ children, aksi }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 10 }}>
        <span style={{ fontSize: 12.5, color: 'var(--s-ink-3)' }}>{children}</span>
        {aksi}
    </div>
);

/* Medan boleh taip. Sunting = bingkai lebih terang; baca-sahaja = isian
   pudar. Beza itu sahaja yang membezakan kedua-dua keadaan — tiada warna
   jenama pada kawalan (lihat peraturan token). */
const Inp = ({ sunting = false, style, ...rest }) => (
    <input
        {...rest}
        style={{
            width: '100%', height: 38, borderRadius: 8, boxSizing: 'border-box',
            border: `1px solid ${sunting ? 'var(--s-ink-3)' : 'var(--s-line-strong)'}`,
            background: sunting ? 'transparent' : 'var(--s-fill)',
            color: sunting ? 'var(--s-ink)' : 'var(--s-ink-2)',
            padding: '0 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none',
            ...style,
        }}
    />
);

/* Medan dengan ikon di kiri. */
const InpIkon = ({ ikon, ...rest }) => (
    <div style={{ position: 'relative' }}>
        <span style={{
            position: 'absolute', left: 12, top: 0, bottom: 0, display: 'flex',
            alignItems: 'center', color: 'var(--s-ink-3)', pointerEvents: 'none',
        }}>{ikon}</span>
        <Inp {...rest} style={{ paddingLeft: 34, ...(rest.style || {}) }} />
    </div>
);

const Baris = ({ label, children }) => (
    <div>
        <div style={{ fontSize: 12.5, color: 'var(--s-ink-3)', marginBottom: 6 }}>{label}</div>
        <div style={{
            display: 'flex', alignItems: 'center', gap: 8, minHeight: 38,
            padding: '0 12px', borderRadius: 8,
            border: '1px solid var(--s-line)', background: 'var(--s-fill)',
            fontSize: 14, color: 'var(--s-ink)',
        }}>{children}</div>
    </div>
);

const Dua = ({ children }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        {children}
    </div>
);

/* Bar penggunaan. Putih pada keadaan biasa — pada latar hampir hitam
   putih ialah aksen paling kuat; warna status disimpan untuk keadaan
   yang benar-benar memerlukan perhatian. */
const Meter = ({ ratio }) => {
    const pct = Math.min(100, Math.max(0, ratio * 100));
    const warna = ratio > 0.9 ? 'var(--s-stop)' : ratio > 0.7 ? 'var(--s-warn)' : 'var(--s-ink)';
    return (
        <div style={{ height: 6, borderRadius: 99, background: 'var(--s-fill)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: warna, borderRadius: 99, transition: 'width .4s ease' }} />
        </div>
    );
};

/* Satu blok kuota slot: angka besar + baki + bar penggunaan.
   Dipakai DUA kali (browser Chrome dan avatar) daripada satu takrifan —
   dua salinan pasti terpesong, dan pelanggan akan nampak dua gaya
   berlainan untuk perkara yang sama. */
const Slot = ({ label, guna, had, tanpaHad, teksHad, teksTanpaHad, teksTakTahu }) => (
    <div>
        <div style={{ fontSize: 12.5, color: 'var(--s-ink-3)', marginBottom: 6 }}>{label}</div>
        {guna === null || guna === undefined ? (
            <div style={{ display: 'flex', alignItems: 'center', minHeight: 44 }}>
                <span className="s-dim" style={{ fontSize: 13 }}>{teksTakTahu}</span>
            </div>
        ) : (
            <>
                <div style={{
                    display: 'flex', alignItems: 'baseline',
                    justifyContent: tanpaHad ? 'flex-start' : 'space-between',
                    gap: 8, marginBottom: 10,
                }}>
                    <span style={{ fontSize: 26, fontWeight: 600, color: 'var(--s-ink)', letterSpacing: '-.02em' }}>{guna}</span>
                    <span className="s-dim">{tanpaHad ? teksTanpaHad : teksHad}</span>
                </div>
                {/* Tanpa had = tiada bar. Bar yang tidak pernah penuh hanya
                    menimbulkan soalan "kenapa ia sentiasa kosong?" */}
                {!tanpaHad && <Meter ratio={had > 0 ? guna / had : 0} />}
            </>
        )}
    </div>
);

/* Mesej hasil operasi. */
const Nota = ({ jenis, children }) => {
    const warna = jenis === 'error' ? 'var(--s-stop)' : jenis === 'success' ? 'var(--s-ok)' : 'var(--s-ink-2)';
    const isian = jenis === 'error' ? 'var(--s-stop-soft)' : jenis === 'success' ? 'var(--s-ok-soft)' : 'var(--s-fill)';
    return (
        <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 12px',
            borderRadius: 8, background: isian, color: warna, fontSize: 12.5, lineHeight: 1.5,
        }}>
            {jenis === 'success' ? <Check size={13} style={{ marginTop: 2, flex: 'none' }} />
                : <Info size={13} style={{ marginTop: 2, flex: 'none' }} />}
            <span>{children}</span>
        </div>
    );
};

/* ── Pemilih tema (halaman Paparan) ────────────────────────────────
   Corak tile cal.com/settings/my-account/appearance: pratonton kecil
   + label beradio di bawahnya, tile terpilih dibingkai tebal. Warna
   dalam pratonton SENGAJA ditulis tetap — setiap tile menggambarkan
   temanya SENDIRI, bukan tema yang sedang aktif. */
const PratontonTema = ({ gelap }) => {
    const c = gelap
        ? { ground: '#111111', card: '#181818', line: 'rgba(255,255,255,.10)', bar: 'rgba(255,255,255,.30)', barKuat: '#FAFAFA' }
        : { ground: '#FAFAFA', card: '#FFFFFF', line: 'rgba(0,0,0,.10)', bar: 'rgba(0,0,0,.22)', barKuat: '#262626' };
    return (
        <span style={{ display: 'flex', width: '100%', height: '100%', background: c.ground }}>
            <span style={{ width: 34, flex: 'none', borderRight: `1px solid ${c.line}`, padding: '8px 6px', display: 'grid', gap: 4, alignContent: 'start' }}>
                <span style={{ height: 4, borderRadius: 2, background: c.bar }} />
                <span style={{ height: 4, width: '80%', borderRadius: 2, background: c.bar }} />
                <span style={{ height: 4, width: '65%', borderRadius: 2, background: c.bar }} />
            </span>
            <span style={{ flex: 1, padding: 8, display: 'grid', gap: 5, alignContent: 'start' }}>
                <span style={{ height: 5, width: '45%', borderRadius: 2, background: c.barKuat }} />
                <span style={{ border: `1px solid ${c.line}`, background: c.card, borderRadius: 5, padding: 6, display: 'grid', gap: 4, alignContent: 'start' }}>
                    <span style={{ height: 4, width: '70%', borderRadius: 2, background: c.bar }} />
                    <span style={{ height: 4, width: '50%', borderRadius: 2, background: c.bar }} />
                </span>
            </span>
        </span>
    );
};

const TilTema = ({ aktif, label, onPilih, children }) => (
    <button type="button" onClick={onPilih} style={{
        background: 'none', border: 0, padding: 0, cursor: 'pointer',
        fontFamily: 'inherit', display: 'grid', gap: 8, justifyItems: 'start', textAlign: 'left',
    }}>
        <span style={{
            display: 'block', width: 150, height: 94, borderRadius: 10, overflow: 'hidden', boxSizing: 'border-box',
            border: aktif ? '2px solid var(--s-ink)' : '1px solid var(--s-line-strong)',
        }}>{children}</span>
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13,
            fontWeight: aktif ? 600 : 400, color: aktif ? 'var(--s-ink)' : 'var(--s-ink-2)',
        }}>
            <span style={{
                width: 13, height: 13, borderRadius: 99, boxSizing: 'border-box', flex: 'none',
                border: aktif ? '4px solid var(--s-ink)' : '1.5px solid var(--s-ink-3)',
            }} />
            {label}
        </span>
    </button>
);

/* Pemilih bahasa — corak tile yang SAMA dengan pemilih tema di atas,
   supaya kedua-dua pilihan pada halaman Paparan kelihatan sekeluarga.
   Pratontonnya menunjukkan nama bahasa dalam bahasa itu sendiri
   (endonim): pelanggan yang tersalah pilih masih boleh mengenali
   bahasanya untuk patah balik. */
const PratontonBahasa = ({ kod, label }) => (
    <span style={{
        display: 'grid', placeItems: 'center', gap: 4, width: '100%', height: '100%',
        background: 'var(--s-fill)', alignContent: 'center',
    }}>
        <span style={{
            fontSize: 21, fontWeight: 600, letterSpacing: '.06em',
            color: 'var(--s-ink)', textTransform: 'uppercase',
        }}>{kod}</span>
        <span style={{ fontSize: 11, color: 'var(--s-ink-3)' }}>{label}</span>
    </span>
);

/* Pautan sunting kecil di hujung label — "Tukar" / "Simpan · Batal". */
const AksiSunting = ({ sedang, menyimpan, onMula, onSimpan, onBatal, labelMula, IkonSimpan = Save }) => {
    const t = useT();
    return sedang ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <button type="button" onClick={onSimpan} disabled={menyimpan}
                style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 12.5, color: 'var(--s-ink)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                {menyimpan ? <RefreshCw size={12} /> : <IkonSimpan size={12} />} {t('common.save')}
            </button>
            <button type="button" onClick={onBatal}
                style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 12.5, color: 'var(--s-ink-3)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <X size={12} /> {t('common.cancel')}
            </button>
        </span>
    ) : (
        <button type="button" onClick={onMula}
            style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 12.5, color: 'var(--s-ink-2)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Pencil size={12} /> {labelMula || t('common.change')}
        </button>
    );
};

export default function SettingsV39View({ modSebenar = false, initialTab = null }) {
    const navigate = useNavigate();
    const t = useT();
    const { lang, setLang } = useLang();
    /* `uiV39: true` — fail ini rupa v3.9, jadi mesej akaun daripada hook
       ikut suis bahasa. ProfileView (rupa lama) tidak menghantarnya, jadi
       ia kekal Melayu. */
    const langsung = useAccountSettings({ enabled: modSebenar, uiV39: true });
    const s = modSebenar ? langsung : CONTOH;

    /* Mod pelan tiada alamat per halaman — ia hidup dalam satu route,
       jadi halaman aktif disimpan sebagai keadaan tempatan. */
    const [tabPelan, setTabPelan] = useState('account');
    const rawTab = modSebenar ? (initialTab || 'account') : tabPelan;
    const activeTab = TAB_ALIAS[rawTab] || rawTab;
    const pergiTab = (id) => (modSebenar ? navigate(`/profile/${id}`) : setTabPelan(id));

    /* Tema v3.9 — kelas `s-light` pada <html> supaya semua halaman v3.9
       (termasuk modal portal di bawah <body>) bertukar serentak; muat
       halaman berikutnya dipasang semula oleh main.jsx. Serta-merta,
       tiada butang Simpan; per-browser (localStorage), bukan Firestore.
       Sengaja hidup dalam mod pelan juga — ia pilihan browser yang
       tidak menyentuh akaun sesiapa. */
    const [temaV39, setTemaV39] = useState(() =>
        document.documentElement.classList.contains('s-light') ? 'light' : 'dark');
    const pilihTema = (mode) => {
        document.documentElement.classList.toggle('s-light', mode === 'light');
        try {
            localStorage.setItem('sesaat_theme_v39', mode);
        } catch { /* storan disekat (mod privasi) — pilihan hidup untuk sesi ini sahaja */ }
        setTemaV39(mode);
    };

    useEffect(() => {
        if (!modSebenar) return;
        if (initialTab && TAB_ALIAS[initialTab]) navigate(`/profile/${TAB_ALIAS[initialTab]}`, { replace: true });
    }, [modSebenar, initialTab, navigate]);

    useEffect(() => {
        if (!modSebenar || activeTab !== 'extension') return;
        s.runExtProbe();
    }, [modSebenar, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    /* Struktur menu mengikut rel StudioShell: label kumpulan kecil-huruf-besar,
       kemudian baris NavRow berikon. Ikon duduk pada BARIS, bukan pada label —
       itu yang membezakannya daripada cal.com, dan ia sengaja: rel prototaip
       memang begitu, jadi menu setting tidak kelihatan datang dari app lain. */
    const kumpulan = [
        {
            id: 'akaun', label: t('settings.group.account'), items: [
                { id: 'account', label: t('settings.nav.account'), icon: User },
                { id: 'appearance', label: t('settings.nav.appearance'), icon: Palette },
            ]
        },
        { id: 'keselamatan', label: t('settings.group.security'), items: [{ id: 'security', label: t('settings.nav.security'), icon: Shield }] },
        {
            id: 'langganan', label: t('settings.group.subscription'), items: [
                { id: 'billing', label: t('settings.nav.billing'), icon: CreditCard },
                { id: 'storage', label: t('settings.nav.storage'), icon: HardDrive },
            ]
        },
        { id: 'ext', label: t('settings.group.extension'), items: [{ id: 'extension', label: t('settings.nav.extension'), icon: Puzzle }] },
        ...(s.isAdmin ? [] : [{ id: 'rujukan', label: t('settings.group.referral'), items: [{ id: 'referral', label: t('settings.nav.referral'), icon: Users }] }]),
    ];

    const halaman = HALAMAN_SAH.includes(activeTab) ? activeTab : 'account';
    const tierLabel = TIER_LABEL[s.tier] || TIER_LABEL.free;
    /* Slot + modul ditarik keluar daripada objek tetapan supaya JSX di bawah
       tidak menambah lagi amaran palsu react-hooks/refs (peraturan itu membaca
       setiap capaian s.xxx dalam JSX sebagai capaian ref — 158 amaran sedia ada). */
    const { browserDigunakan, kuotaSlot, slotTanpaHad, avatarDigunakan, avatarKuota, modulSaya } = s;
    const berbayarBukanAdmin = s.isPaid && !s.isAdmin;

    /* Langganan Flow (berasingan dari plan utama) — data dari endpoint yang SAMA dengan halaman
       Flow Google (/customer/overview). Mod pelan reka bentuk (!modSebenar) tidak memanggil server.
       Status guna kunci kongsi fc.sub* (aktif/hampir tamat/tamat). */
    const flow = useFlowLangganan(modSebenar);
    /* Tarik ke pemboleh ubah tempatan — elak tambah amaran palsu react-hooks/refs (lihat nota slot/modul atas). */
    const { loading: flowLoading, error: flowError, sub: flowSub, stat: flowStat } = flow;
    const { productName: flowNama, expiresAt: flowTamat } = flowSub || {};
    const [flowTone, flowKunci] = flowStat || [];

    const pelanTeks = `${tierLabel}${s.isPaid && s.plan && !s.isAdmin ? ` · ${t(s.plan === 'yearly' ? 'settings.billing.yearly' : 'settings.billing.monthly')}` : ''}`;

    if (modSebenar && s.loading) {
        return (
            <div className="s-root" style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'var(--s-ground)', display: 'grid', placeItems: 'center' }}>
                <span className="s-dim">{t('settings.loading')}</span>
            </div>
        );
    }

    /* ── Kandungan setiap halaman ───────────────────────────────────── */

    const halamanProfil = (
        <>
            <Card style={{ marginBottom: 16 }}>
                <CardBody style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <span style={{
                        width: 56, height: 56, borderRadius: 99, flex: 'none', overflow: 'hidden',
                        display: 'grid', placeItems: 'center',
                        background: 'var(--s-brand-soft)', color: 'var(--s-brand)', fontSize: 17, fontWeight: 600,
                    }}>
                        {s.currentPhotoURL
                            ? <img src={s.currentPhotoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : s.initials}
                    </span>
                    <div style={{ flex: 1, minWidth: 180 }}>
                        <div className="s-cardname" style={{ marginBottom: 8 }}>{t('settings.profile.photo')}</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <Btn size="sm" variant="ghost" icon={<Camera size={13} />}
                                disabled={s.avatarUploading}
                                onClick={() => s.avatarInputRef.current?.click()}>
                                {t('settings.profile.upload')}
                            </Btn>
                            {s.currentPhotoURL && (
                                <Btn size="sm" variant="quiet" icon={<Trash2 size={13} />}
                                    disabled={s.avatarUploading} onClick={s.handleRemoveAvatar}>
                                    {t('settings.profile.remove')}
                                </Btn>
                            )}
                        </div>
                        <p className="s-dim" style={{ fontSize: 12, marginTop: 8 }}>{t('settings.profile.photoHint')}</p>
                    </div>
                    <input ref={s.avatarInputRef} type="file" accept="image/*" onChange={s.handleAvatarUpload} style={{ display: 'none' }} />
                </CardBody>
            </Card>

            <Card style={{ marginBottom: 16 }}>
                <CardBody style={{ display: 'grid', gap: 18 }}>
                    <div>
                        <Lbl aksi={<AksiSunting sedang={s.isEditingName} menyimpan={s.nameSaving}
                            onMula={s.startEditName} onSimpan={s.handleSaveName} onBatal={s.cancelEditName} />}>
                            {t('settings.profile.displayName')}
                        </Lbl>
                        {s.isEditingName ? (
                            <Inp sunting value={s.editName} autoFocus
                                onChange={(e) => s.setEditName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') s.handleSaveName(); if (e.key === 'Escape') s.cancelEditName(); }} />
                        ) : (
                            <Inp value={s.displayName} readOnly />
                        )}
                        {s.nameMsg.text && <div style={{ marginTop: 8 }}><Nota jenis={s.nameMsg.type}>{s.nameMsg.text}</Nota></div>}
                    </div>

                    <div>
                        <Lbl aksi={<AksiSunting sedang={s.isEditingEmail} menyimpan={s.emailSaving}
                            onMula={s.startEditEmail} onSimpan={s.handleSaveEmail} onBatal={s.cancelEditEmail}
                            IkonSimpan={Send} />}>
                            {t('settings.profile.email')}
                        </Lbl>
                        {s.isEditingEmail ? (
                            <>
                                <InpIkon ikon={<Mail size={14} />} sunting value={s.editEmail} autoFocus
                                    placeholder={t('settings.profile.emailPlaceholder')}
                                    onChange={(e) => s.setEditEmail(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') s.handleSaveEmail(); if (e.key === 'Escape') s.cancelEditEmail(); }} />
                                <div style={{ marginTop: 8 }}>
                                    <Nota>{t('settings.profile.emailNote')}</Nota>
                                </div>
                            </>
                        ) : (
                            <InpIkon ikon={<Mail size={14} />} value={s.user?.email || ''} readOnly />
                        )}
                        {s.emailMsg.text && <div style={{ marginTop: 8 }}><Nota jenis={s.emailMsg.type}>{s.emailMsg.text}</Nota></div>}
                    </div>

                    <div>
                        <Lbl aksi={<AksiSunting sedang={s.isEditingWhatsapp} menyimpan={s.whatsappSaving}
                            onMula={s.startEditWhatsapp} onSimpan={s.handleSaveWhatsapp} onBatal={s.cancelEditWhatsapp} />}>
                            {t('settings.profile.whatsapp')}
                        </Lbl>
                        {s.isEditingWhatsapp ? (
                            <>
                                <InpIkon ikon={<MessageCircle size={14} />} sunting value={s.editWhatsapp} autoFocus
                                    inputMode="numeric" placeholder={t('settings.profile.whatsappPlaceholder')}
                                    style={{ fontFamily: 'var(--s-mono)' }}
                                    onChange={(e) => s.setEditWhatsapp(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') s.handleSaveWhatsapp(); if (e.key === 'Escape') s.cancelEditWhatsapp(); }} />
                                <p className="s-dim" style={{ fontSize: 12, marginTop: 7 }}>
                                    {t('settings.profile.whatsappNote')}
                                </p>
                            </>
                        ) : (
                            <InpIkon ikon={<MessageCircle size={14} />} value={s.userData?.whatsappNumber || ''}
                                readOnly placeholder={t('common.noNumber')} style={{ fontFamily: 'var(--s-mono)' }} />
                        )}
                        {s.whatsappMsg.text && <div style={{ marginTop: 8 }}><Nota jenis={s.whatsappMsg.type}>{s.whatsappMsg.text}</Nota></div>}
                    </div>
                </CardBody>
            </Card>

            <Card>
                <CardBody>
                    <Dua>
                        <Baris label={t('settings.profile.package')}><Badge tone={s.isAdmin ? 'brand' : 'neutral'}>{pelanTeks}</Badge></Baris>
                        <Baris label={t('settings.profile.memberSince')}><span style={{ fontSize: 14 }}>{s.joinedDate}</span></Baris>
                    </Dua>
                </CardBody>
            </Card>
        </>
    );

    const halamanPaparan = (
        <>
            <Card style={{ marginBottom: 16 }}>
                <CardBody>
                    <h3 className="s-cardname">{t('settings.appearance.themeTitle')}</h3>
                    <p className="s-dim" style={{ marginTop: 3, marginBottom: 16 }}>
                        {t('settings.appearance.themeDesc')}
                    </p>
                    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                        <TilTema label={t('settings.appearance.dark')} aktif={temaV39 === 'dark'} onPilih={() => pilihTema('dark')}>
                            <PratontonTema gelap />
                        </TilTema>
                        <TilTema label={t('settings.appearance.light')} aktif={temaV39 === 'light'} onPilih={() => pilihTema('light')}>
                            <PratontonTema gelap={false} />
                        </TilTema>
                    </div>
                </CardBody>
                <CardFoot>
                    <span className="s-dim" style={{ fontSize: 12.5 }}>
                        {t('settings.appearance.themeFoot')}
                    </span>
                </CardFoot>
            </Card>

            {/* Pemilih bahasa — sengaja kad BERASINGAN daripada tema, dan
                sengaja guna komponen tile yang SAMA (TilTema). Dua pilihan
                yang berlainan sifatnya tidak dicampur dalam satu kad, tetapi
                keduanya kekal kelihatan sekeluarga. Serta-merta, tiada butang
                Simpan — sama seperti tema. */}
            <Card>
                <CardBody>
                    <h3 className="s-cardname">{t('settings.appearance.langTitle')}</h3>
                    <p className="s-dim" style={{ marginTop: 3, marginBottom: 16 }}>
                        {t('settings.appearance.langDesc')}
                    </p>
                    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                        {SENARAI_BAHASA.map((b) => (
                            <TilTema key={b.id} label={b.label} aktif={lang === b.id} onPilih={() => setLang(b.id)}>
                                <PratontonBahasa kod={b.kod} label={b.label} />
                            </TilTema>
                        ))}
                    </div>
                </CardBody>
                <CardFoot>
                    <span className="s-dim" style={{ fontSize: 12.5 }}>
                        {t('settings.appearance.langFoot')}
                    </span>
                </CardFoot>
            </Card>
        </>
    );

    const halamanKataLaluan = (
        <Card>
            <CardBody style={{ display: 'grid', gap: 16 }}>
                {!s.hasPassword && (
                    <Nota>
                        {t('settings.password.googleOnly', { emel: s.user?.email || '' })}
                    </Nota>
                )}

                {s.hasPassword && (
                    <div>
                        <Lbl>{t('settings.password.current')}</Lbl>
                        <div style={{ position: 'relative' }}>
                            <Inp sunting type={s.showPasswords.current ? 'text' : 'password'} placeholder="••••••••••••"
                                value={s.passwordData.current} style={{ paddingRight: 38 }}
                                onChange={(e) => s.setPasswordData(p => ({ ...p, current: e.target.value }))} />
                            <button type="button" onClick={() => s.setShowPasswords(p => ({ ...p, current: !p.current }))}
                                style={{ position: 'absolute', right: 10, top: 0, bottom: 0, background: 'none', border: 0, cursor: 'pointer', color: 'var(--s-ink-3)' }}>
                                {s.showPasswords.current ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                        </div>
                    </div>
                )}

                <Dua>
                    <div>
                        <Lbl>{t('settings.password.new')}</Lbl>
                        <div style={{ position: 'relative' }}>
                            <Inp sunting type={s.showPasswords.new ? 'text' : 'password'} placeholder={t('settings.password.new')}
                                value={s.passwordData.new} style={{ paddingRight: 38 }}
                                onChange={(e) => s.setPasswordData(p => ({ ...p, new: e.target.value }))} />
                            <button type="button" onClick={() => s.setShowPasswords(p => ({ ...p, new: !p.new }))}
                                style={{ position: 'absolute', right: 10, top: 0, bottom: 0, background: 'none', border: 0, cursor: 'pointer', color: 'var(--s-ink-3)' }}>
                                {s.showPasswords.new ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                        </div>
                    </div>
                    <div>
                        <Lbl>{t('settings.password.confirm')}</Lbl>
                        <div style={{ position: 'relative' }}>
                            <Inp sunting type={s.showPasswords.confirm ? 'text' : 'password'} placeholder={t('settings.password.retype')}
                                value={s.passwordData.confirm} style={{ paddingRight: 38 }}
                                onChange={(e) => s.setPasswordData(p => ({ ...p, confirm: e.target.value }))} />
                            <button type="button" onClick={() => s.setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                                style={{ position: 'absolute', right: 10, top: 0, bottom: 0, background: 'none', border: 0, cursor: 'pointer', color: 'var(--s-ink-3)' }}>
                                {s.showPasswords.confirm ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                        </div>
                    </div>
                </Dua>

                {s.passwordData.new && (
                    <div style={{ display: 'grid', gap: 6 }}>
                        <Meter ratio={s.passwordStrength.pct / 100} />
                        <span className="s-dim" style={{ fontSize: 12 }}>{t('settings.password.strength', { tahap: s.passwordStrength.label })}</span>
                    </div>
                )}

                {s.passwordValidation.mismatch && <Nota jenis="error">{t('settings.password.mismatch')}</Nota>}
                {s.passwordValidation.matches && <Nota jenis="success">{t('settings.password.matches')}</Nota>}

                <p className="s-dim" style={{ fontSize: 12 }}>
                    {t('settings.password.rule')}
                </p>

                {s.passwordMsg.text && <Nota jenis={s.passwordMsg.type}>{s.passwordMsg.text}</Nota>}
                {s.resetEmailSent && (
                    <p className="s-dim" style={{ fontSize: 12 }}>
                        {t('settings.password.checkInbox', { emel: s.user?.email || '' })}
                    </p>
                )}
            </CardBody>
            <CardFoot>
                <span className="s-dim" style={{ fontSize: 12.5 }}>
                    {t(s.hasPassword ? 'settings.password.stayLoggedIn' : 'settings.password.emailIsUsername')}
                </span>
                <span style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {s.hasPassword && (
                        <Btn size="sm" variant="ghost" icon={<Send size={13} />}
                            disabled={s.resetEmailLoading || s.resetEmailSent}
                            onClick={s.handleResetPasswordEmail}>
                            {t(s.resetEmailSent ? 'settings.password.resetSent' : 'settings.password.sendReset')}
                        </Btn>
                    )}
                    <Btn size="sm" disabled={!s.canSubmitPassword}
                        onClick={s.hasPassword ? s.handleChangePassword : s.handleSetPassword}>
                        {s.passwordLoading ? t('common.saving') : t(s.hasPassword ? 'settings.password.update' : 'settings.password.set')}
                    </Btn>
                </span>
            </CardFoot>
        </Card>
    );

    const halamanPakej = (
        <>
            <Card style={{ marginBottom: 16 }}>
                <CardBody>
                    <Dua>
                        <Baris label={t('settings.billing.currentPlan')}><Badge tone={s.isAdmin ? 'brand' : 'neutral'}>{pelanTeks}</Badge></Baris>
                        <Baris label={t('settings.billing.activeUntil')}>
                            <span style={{ fontSize: 14 }}>{s.isAdmin ? t('common.unlimited') : (s.expiryDate || t('common.noRecord'))}</span>
                        </Baris>
                    </Dua>
                </CardBody>
                {s.isPaid && !s.isAdmin && (
                    <CardFoot>
                        <span className="s-dim" style={{ fontSize: 12.5 }}>{t('settings.billing.manageFoot')}</span>
                        <Btn size="sm" variant="ghost" onClick={() => modSebenar && navigate('/pricing')}>
                            {t('settings.billing.manage')}
                        </Btn>
                    </CardFoot>
                )}
            </Card>

            {/* Slot & modul. SATU nombor lesen (maxProfiles) mengawal slot browser
                DAN slot avatar — itu memang kelakuan sistem, jadi kedua-duanya
                dipapar bersebelahan supaya pelanggan nampak ia satu kuota. */}
            <Card style={{ marginBottom: 16 }}>
                <CardBody>
                    <h3 className="s-cardname">{t('settings.billing.slotTitle')}</h3>
                    <p className="s-dim" style={{ marginTop: 3, marginBottom: 16 }}>{t('settings.billing.slotDesc')}</p>
                    <Dua>
                        <Slot
                            label={t('settings.billing.slotBrowser')}
                            guna={browserDigunakan}
                            had={kuotaSlot}
                            tanpaHad={slotTanpaHad}
                            teksHad={t('settings.billing.slotOf', { n: kuotaSlot })}
                            teksTanpaHad={t('settings.billing.slotUnlimited')}
                            teksTakTahu={t('settings.billing.slotUnknown')}
                        />
                        <Slot
                            label={t('settings.billing.slotAvatar')}
                            guna={avatarDigunakan}
                            had={avatarKuota}
                            tanpaHad={false}
                            teksHad={t('settings.billing.slotOf', { n: avatarKuota })}
                            teksTanpaHad={t('settings.billing.slotUnlimited')}
                            teksTakTahu={t('settings.billing.slotUnknown')}
                        />
                    </Dua>
                </CardBody>
                {berbayarBukanAdmin && (
                    <CardFoot>
                        <span className="s-dim" style={{ fontSize: 12.5 }}>{t('settings.billing.slotFoot')}</span>
                    </CardFoot>
                )}
            </Card>

            {/* Modul yang pelanggan betul-betul ada. Nama pemasaran, bukan id
                dalaman — 'ai16saat-half' tidak bermakna apa-apa kepada pelanggan. */}
            <Card style={{ marginBottom: 16 }}>
                <CardBody>
                    <h3 className="s-cardname">{t('settings.billing.modulesTitle')}</h3>
                    <p className="s-dim" style={{ marginTop: 3, marginBottom: 16 }}>{t('settings.billing.modulesDesc')}</p>
                    {modulSaya.length ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {modulSaya.map((m) => (
                                <Badge key={m.id} tone="neutral">{m.nama}</Badge>
                            ))}
                        </div>
                    ) : (
                        <p className="s-meta" style={{ fontSize: 13.5 }}>{t('settings.billing.modulesNone')}</p>
                    )}
                </CardBody>
            </Card>

            {/* PRO Studio — claim ciri percuma hingga 30 Nov 2026 (keputusan user 26 Sept). */}
            <KadProStudioPercuma modSebenar={modSebenar} t={t} bahasa={lang} />

            {/* Langganan Flow — berasingan dari plan utama. Butang pergi ke halaman Flow Google,
                tab Subscription & Billing (deep-link /flow-google/billing). Sentiasa dipapar
                (keputusan user 4 Okt): tiada Flow → ajakan "Get Flow". */}
            <Card style={{ marginBottom: 16 }}>
                <CardBody>
                    <h3 className="s-cardname">{t('settings.billing.flowTitle')}</h3>
                    <p className="s-dim" style={{ marginTop: 3, marginBottom: 16 }}>{t('settings.billing.flowDesc')}</p>
                    {flowLoading ? (
                        <p className="s-meta" style={{ fontSize: 13.5 }}>{t('settings.billing.flowChecking')}</p>
                    ) : flowError ? (
                        <p className="s-meta" style={{ fontSize: 13.5 }}>{t('settings.billing.flowUnavailable')}</p>
                    ) : flowSub ? (
                        <Dua>
                            <Baris label={t('settings.billing.flowPlan')}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 14 }}>{flowNama || t('fc.planType1')}</span>
                                    {flowStat && <Badge tone={flowTone}>{t(flowKunci)}</Badge>}
                                </span>
                            </Baris>
                            <Baris label={t('settings.billing.activeUntil')}>
                                <span style={{ fontSize: 14 }}>{flowTamat ? tarikhPapar(flowTamat, lang) : t('common.noRecord')}</span>
                            </Baris>
                        </Dua>
                    ) : (
                        <p className="s-meta" style={{ fontSize: 13.5 }}>{t('settings.billing.flowNone')}</p>
                    )}
                </CardBody>
                <CardFoot>
                    <span className="s-dim" style={{ fontSize: 12.5 }}>{flowSub ? t('settings.billing.flowManageFoot') : t('settings.billing.flowGetFoot')}</span>
                    {flowSub
                        ? <Btn size="sm" variant="ghost" onClick={() => modSebenar && navigate('/flow-google/billing')}>{t('settings.billing.flowManage')}</Btn>
                        : <Btn size="sm" onClick={() => modSebenar && navigate('/flow')}>{t('settings.billing.flowGet')}</Btn>}
                </CardFoot>
            </Card>

            {/* Pelanggan pakej SMART — laluan naik taraf RM100 sekali bayar.
                ⛔ Butang ini SENGAJA menghantar pelanggan ke halaman harga, bukan
                   mencipta bil sendiri. Dua tempat yang mencipta bil untuk perkara
                   sama pasti terpesong; halaman harga sudah memegang laluan itu. */}
            {s.isPaid && !s.isAdmin && s.licType === 'smart' && (
                <Card style={{ marginBottom: 16 }}>
                    <CardBody>
                        <h3 className="s-cardname" style={{ marginBottom: 4 }}>{t('settings.billing.smartTitle')}</h3>
                        <p className="s-meta" style={{ fontSize: 13.5 }}>{t('settings.billing.smartDesc')}</p>
                    </CardBody>
                    <CardFoot>
                        <span className="s-dim" style={{ fontSize: 12.5 }}>{t('settings.billing.smartFoot')}</span>
                        <Btn size="sm" onClick={() => modSebenar && navigate('/pricing')}>{t('pricing.cta.upgradeSmart')}</Btn>
                    </CardFoot>
                </Card>
            )}

            {/* CTA untuk pelanggan belum berbayar. Ayat merujuk produk SESAAT yang
                sebenar — versi lama tertulis "unlimited rewrites, 10x transcriptions"
                yang bukan produk kita langsung. */}
            {!s.isPaid && (
                <Card>
                    <CardBody>
                        <h3 className="s-cardname" style={{ marginBottom: 4 }}>{t('settings.billing.upgradeTitle')}</h3>
                        <p className="s-meta" style={{ fontSize: 13.5 }}>
                            {t('settings.billing.upgradeDesc', { kuota: DAILY_PAID_CAP })}
                        </p>
                    </CardBody>
                    <CardFoot>
                        <span className="s-dim" style={{ fontSize: 12.5 }}>{t('settings.billing.upgradeFoot')}</span>
                        <Btn size="sm" onClick={() => modSebenar && navigate('/pricing')}>{t('settings.billing.seePricing')}</Btn>
                    </CardFoot>
                </Card>
            )}
        </>
    );

    const halamanStorage = (
        <>
            <Card style={{ marginBottom: 16 }}>
                <CardBody>
                    <h3 className="s-cardname">{t('settings.storage.title')}</h3>
                    <p className="s-dim" style={{ marginTop: 3, marginBottom: 16 }}>{t('settings.storage.desc')}</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
                        <span style={{ fontSize: 26, fontWeight: 600, color: 'var(--s-ink)', letterSpacing: '-.02em' }}>
                            {fmtBytes(s.storageUsed)}
                        </span>
                        <span className="s-dim">{t('settings.storage.of', { jumlah: fmtBytes(STORAGE_LIMIT_BYTES) })}</span>
                    </div>
                    <Meter ratio={s.storageRatio} />
                    <p className="s-meta" style={{ fontSize: 13, marginTop: 12 }}>
                        {t(s.storageRatio > 0.9 ? 'settings.storage.almostFull' : 'settings.storage.hint')}
                    </p>
                </CardBody>
                <CardFoot>
                    <span className="s-dim" style={{ fontSize: 12.5 }}>{t('settings.storage.foot')}</span>
                    <Btn size="sm" variant="ghost" icon={<HardDrive size={13} />}
                        onClick={() => modSebenar && navigate('/workspace')}>
                        {t('settings.storage.manage')}
                    </Btn>
                </CardFoot>
            </Card>

            <Card>
                <CardBody>
                    <h3 className="s-cardname">{t('settings.storage.quotaTitle')}</h3>
                    <p className="s-dim" style={{ marginTop: 3, marginBottom: 16 }}>{t('settings.storage.quotaDesc')}</p>

                    {s.isAdmin ? (
                        <Baris label={t('common.today')}>
                            <Gauge size={14} style={{ color: 'var(--s-ink-3)' }} />
                            <span style={{ fontSize: 14 }}>{t('settings.storage.quotaAdmin', { n: s.usedToday })}</span>
                        </Baris>
                    ) : s.isPaid ? (
                        <>
                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
                                <span style={{ fontSize: 26, fontWeight: 600, color: 'var(--s-ink)', letterSpacing: '-.02em' }}>{s.usedToday}</span>
                                <span className="s-dim">{t('settings.storage.quotaOfToday', { n: DAILY_PAID_CAP })}</span>
                            </div>
                            <Meter ratio={s.usedToday / DAILY_PAID_CAP} />
                            <p className="s-meta" style={{ fontSize: 13, marginTop: 12 }}>
                                {t('settings.storage.quotaResetNote')}
                            </p>
                        </>
                    ) : (
                        <>
                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
                                <span style={{ fontSize: 26, fontWeight: 600, color: 'var(--s-ink)', letterSpacing: '-.02em' }}>{s.trialCoins ?? 0}</span>
                                <span className="s-dim">{t('settings.storage.quotaOfTrial', { n: TRIAL_COIN_CAP })}</span>
                            </div>
                            <Meter ratio={1 - ((s.trialCoins ?? 0) / TRIAL_COIN_CAP)} />
                            <p className="s-meta" style={{ fontSize: 13, marginTop: 12 }}>
                                {t('settings.storage.quotaTrialNote')}
                            </p>
                        </>
                    )}
                </CardBody>
            </Card>
        </>
    );

    const halamanExtension = (
        <>
            <Card style={{ marginBottom: 16 }}>
                <CardBody>
                    <h3 className="s-cardname">{t('settings.ext.title')}</h3>
                    <p className="s-dim" style={{ marginTop: 3, marginBottom: 16 }}>{t('settings.ext.desc')}</p>

                    <Dua>
                        <Baris label={t('common.status')}>
                            {s.extState.status === 'connected'
                                ? <Badge tone="ok">{t('settings.ext.connected')}</Badge>
                                : s.extState.status === 'checking'
                                    ? <Badge tone="neutral">{t('settings.ext.checking')}</Badge>
                                    : <Badge tone="warn">{t('settings.ext.missing')}</Badge>}
                        </Baris>
                        <Baris label={t('settings.ext.installedVersion')}>
                            <span style={{ fontFamily: 'var(--s-mono)', fontSize: 13.5 }}>
                                {s.extState.status === 'connected' ? (s.extState.version || t('settings.ext.oldVersion')) : '—'}
                            </span>
                        </Baris>
                    </Dua>

                    <p className="s-dim" style={{ fontSize: 12.5, marginTop: 14 }}>
                        {t('settings.ext.latestVersion')} <span style={{ fontFamily: 'var(--s-mono)', color: 'var(--s-ink-2)' }}>{APP_VERSION}</span>
                    </p>

                    {/* Dua extension serentak — punca aduan "saya dah update tapi masih
                        kata versi lama". Yang lama WAJIB dibuang. */}
                    {s.extState.responders > 1 && (
                        <div style={{ marginTop: 14 }}>
                            <Nota jenis="error">
                                {t('settings.ext.duplicateWarn', { laluan: 'chrome://extensions' })}
                            </Nota>
                        </div>
                    )}

                    {s.extState.status === 'missing' && (
                        <div style={{ marginTop: 14 }}>
                            <Nota>
                                {t('settings.ext.missingNote')}
                            </Nota>
                        </div>
                    )}
                </CardBody>
                <CardFoot>
                    <span className="s-dim" style={{ fontSize: 12.5 }}>{t('settings.ext.checkFoot')}</span>
                    <Btn size="sm" variant="ghost" icon={<RefreshCw size={13} />}
                        disabled={s.extState.status === 'checking'} onClick={s.runExtProbe}>
                        {t('settings.ext.recheck')}
                    </Btn>
                </CardFoot>
            </Card>

            <Card>
                <CardBody style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <a href={EXTENSION_ZIP_URL} download className="s-btn s-btn--primary s-btn--sm"
                        style={{ textDecoration: 'none', gap: 8 }}>
                        <Download size={14} /> {t('settings.ext.download')}
                    </a>
                    <Btn size="sm" variant="ghost" onClick={() => modSebenar && navigate('/extension')}>
                        {t('settings.ext.howToInstall')} <ChevronRight size={13} />
                    </Btn>
                    <p className="s-dim" style={{ fontSize: 12, width: '100%', marginTop: 4 }}>
                        {t('settings.ext.storeNote')}
                    </p>
                </CardBody>
            </Card>
        </>
    );

    const halamanRujukan = (
        <Card>
            <CardBody>
                {s.loadingReferrer ? (
                    <span className="s-dim">{t('settings.referral.loading')}</span>
                ) : s.referrerInfo ? (
                    <div style={{ display: 'grid', gap: 14 }}>
                        <Baris label={t('settings.referral.agentName')}>
                            <User size={14} style={{ color: 'var(--s-ink-3)' }} />
                            <span style={{ fontSize: 14 }}>
                                {s.referrerInfo.name || s.referrerInfo.email?.split('@')[0] || t('settings.referral.agentFallback')}
                            </span>
                        </Baris>
                        <Dua>
                            <Baris label={t('settings.referral.code')}>
                                <span style={{ fontFamily: 'var(--s-mono)', fontSize: 13.5 }}>{s.referrerInfo.refCode}</span>
                            </Baris>
                            <div>
                                <div style={{ fontSize: 12.5, color: 'var(--s-ink-3)', marginBottom: 6 }}>{t('settings.referral.contact')}</div>
                                {s.referrerInfo.whatsappNumber ? (
                                    <a href={`https://wa.me/${String(s.referrerInfo.whatsappNumber).replace(/\D/g, '')}`}
                                        target="_blank" rel="noopener noreferrer"
                                        className="s-btn s-btn--ghost s-btn--sm"
                                        style={{ textDecoration: 'none', width: '100%', justifyContent: 'flex-start', height: 38, gap: 8 }}>
                                        <MessageCircle size={14} />
                                        +{String(s.referrerInfo.whatsappNumber).replace(/\D/g, '')}
                                    </a>
                                ) : (
                                    <Baris label=""><span className="s-dim">{t('common.noNumber')}</span></Baris>
                                )}
                            </div>
                        </Dua>
                        <p className="s-dim" style={{ fontSize: 12.5 }}>
                            {t('settings.referral.inaccurate')}
                        </p>
                    </div>
                ) : (
                    <>
                        <h3 className="s-cardname">{t('settings.referral.emptyTitle')}</h3>
                        <p className="s-meta" style={{ fontSize: 13.5, marginTop: 4 }}>
                            {t('settings.referral.emptyDesc')}
                        </p>
                    </>
                )}
            </CardBody>
        </Card>
    );

    const isi = (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px', maxWidth: 720 }}>
            {/* Kepala sendiri (bukan PageHeader) — pil bahasa di hujung kanan,
                kedudukan sama seperti halaman lain. */}
            <div style={{ marginBottom: 22, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                    <h1 className="s-title" style={{ fontSize: 22, marginBottom: 4 }}>{t(`settings.page.${halaman}.title`)}</h1>
                    <p className="s-subtitle">{t(`settings.page.${halaman}.desc`)}</p>
                </div>
                <SuisBahasa />
            </div>

            {halaman === 'account' && halamanProfil}
            {halaman === 'appearance' && halamanPaparan}
            {halaman === 'security' && halamanKataLaluan}
            {halaman === 'billing' && halamanPakej}
            {halaman === 'storage' && halamanStorage}
            {halaman === 'extension' && halamanExtension}
            {halaman === 'referral' && halamanRujukan}

            {!modSebenar && <p className="s-dim" style={{ marginTop: 24 }}>{t('settings.planNote')}</p>}
        </div>
    );

    /* ── Rangka: rel menu kiri + panel kandungan ─────────────────────
       Setting mengambil alih skrin penuh, sama seperti cal.com — rel menu
       utama app duduk di belakangnya, dan itulah sebabnya butang "Kembali"
       wujud. z-index 60 supaya ia berada di atas StudioShell (mod pelan)
       dan rel app sebenar. */
    return (
        /* `sesaat-set-*` — nama kelas WUJUD semata-mata untuk lapisan
           telefon (src/mobile.css seksyen L). Pada telefon rel 264px di
           bawah meninggalkan ~110px sahaja untuk kandungan, jadi rangka ini
           ditukar menjadi menegak: menu jadi jalur mendatar di atas,
           kandungan di bawah. Nama SENDIRI, bukan `.sesaat-studio-rail`
           yang dikongsi rel app — perkongsian nama itulah yang merosakkan
           menu telefon pelanggan pada Ogos 2026. */
        <div className="s-root sesaat-set-shell" style={{
            position: 'fixed', inset: 0, zIndex: 60, display: 'flex',
            background: 'var(--s-ground)', fontFamily: 'var(--s-sans)', color: 'var(--s-ink)',
        }}>
            {/* Rel menu — SETIAP nilai di bawah disalin daripada rel StudioShell
                supaya menu setting tidak kelihatan datang dari app lain:
                  lebar 264 · garis kanan 1px --s-line · padding 16px 12px 14px
                  jurang antara kumpulan 18 · jurang antara baris 2
                  label kumpulan 10px/700/.08em --s-ink-3, padding 0 8px
                  ikon baris 16px strokeWidth 1.9 · baris = NavRow (32px/14px)
                Kalau rel prototaip berubah, ubah di sini juga. */}
            <aside className="sesaat-set-rail" style={{
                width: 264, flex: 'none', height: '100%',
                borderRight: '1px solid var(--s-line)',
                padding: '16px 12px 14px',
                display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0,
                overflowX: 'hidden', overflowY: 'auto',
            }}>
                {/* Kepala — satu tindakan sahaja: "Kembali".
                    ⛔ Bubble avatar DIBUANG (arahan user 2026-08-15), serentak
                       dengan avatar + ikon carian pada baris jenama rel
                       StudioShell. Ia hiasan dari prototaip cal.com — bukan
                       butang, klik tak jadi apa-apa. Avatar sebenar (boleh
                       tukar gambar) memang sudah ada dalam kad Profil. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 4px' }}>
                    <a
                        href={modSebenar ? '/dashboard' : '/dashboard-v3.9'}
                        onClick={(e) => { if (klikBiasa(e)) { e.preventDefault(); navigate(modSebenar ? '/dashboard' : '/dashboard-v3.9'); } }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: 0,
                            background: 'none', border: 0, cursor: 'pointer', textDecoration: 'none',
                            fontFamily: 'inherit', fontSize: 14, fontWeight: 500, color: 'var(--s-ink-2)',
                        }}>
                        <ArrowLeft size={16} strokeWidth={1.9} /> {t('common.back')}
                    </a>
                </div>

                {kumpulan.map((g) => (
                    <div key={g.id} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{
                            fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
                            color: 'var(--s-ink-3)', padding: '0 8px', marginBottom: 4,
                        }}>{g.label}</span>
                        {g.items.map((it) => {
                            const Ikon = it.icon;
                            return (
                                <NavRow key={it.id} active={activeTab === it.id}
                                    icon={<Ikon size={16} strokeWidth={1.9} />}
                                    /* Mod pelan tiada alamat per halaman (satu route,
                                       tab disimpan dalam state) — jadi tiada pautan
                                       untuk dibuka; di situ ia kekal butang. */
                                    href={modSebenar ? `/profile/${it.id}` : undefined}
                                    onClick={() => pergiTab(it.id)}>
                                    {it.label}
                                </NavRow>
                            );
                        })}
                    </div>
                ))}

                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <NavRow icon={<LogOut size={16} strokeWidth={1.9} />} onClick={s.handleLogout}>
                        {t(s.loggingOut ? 'common.loggingOut' : 'common.logout')}
                    </NavRow>
                    <p style={{ fontSize: 11, color: 'var(--s-ink-3)', margin: '10px 0 0', padding: '0 8px' }}>
                        {t('common.copyright', { versi: APP_VERSION })}
                    </p>
                </div>
            </aside>

            <div className="sesaat-set-isi" style={{ flex: 1, minWidth: 0 }}>
                <ContentPanel>{isi}</ContentPanel>
            </div>

            {/* ===== MODAL PENGESAHAN SEMULA ===== */}
            {s.showReauthModal && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 100, display: 'grid', placeItems: 'center',
                    padding: 16, background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)',
                }}>
                    <div style={{
                        width: '100%', maxWidth: 420, background: 'var(--s-card)',
                        border: '1px solid var(--s-line)', borderRadius: 'var(--s-r-lg)', overflow: 'hidden',
                    }}>
                        <div style={{ padding: '20px 20px 0' }}>
                            <h3 className="s-cardname" style={{ fontSize: 15 }}>{t('settings.reauth.title')}</h3>
                            <p className="s-dim" style={{ marginTop: 4 }}>
                                {t('settings.reauth.desc')}
                            </p>
                        </div>
                        <div style={{ padding: 20, display: 'grid', gap: 14 }}>
                            {s.reauthAction === 'email' && (
                                <Nota>
                                    {t('settings.reauth.emailNote', { emel: s.editEmail })}
                                </Nota>
                            )}
                            <div>
                                <Lbl>{t('settings.password.current')}</Lbl>
                                <Inp sunting type="password" autoFocus value={s.reauthPassword}
                                    placeholder={t('settings.reauth.placeholder')}
                                    onChange={(e) => s.setReauthPassword(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') s.handleReauthAndExecute(); if (e.key === 'Escape') s.closeReauthModal(); }} />
                            </div>
                            {s.reauthError && <Nota jenis="error">{s.reauthError}</Nota>}
                        </div>
                        <div className="s-card__foot" style={{ justifyContent: 'flex-end' }}>
                            <Btn size="sm" variant="quiet" onClick={s.closeReauthModal}>{t('common.cancel')}</Btn>
                            <Btn size="sm" disabled={s.reauthLoading || !s.reauthPassword}
                                onClick={s.handleReauthAndExecute}>
                                {t(s.reauthLoading ? 'settings.reauth.verifying' : 'settings.reauth.confirm')}
                            </Btn>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
